'use client'

import React, {useEffect, useMemo, useRef, useState} from 'react'
import Image from 'next/image'

import {optimizeClerkImageUrl} from '../../../lib/clerk-image'
import {cn} from '../../../lib/utils'

type AvatarSize = 'small' | 'medium' | 'large' | 'xlarge'

interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
    name?: string | null
    src?: string | null
    alt?: string
    size?: AvatarSize
    loading?: 'eager' | 'lazy'
    sizes?: string
    cacheBuster?: string | number | null
    fallbackClassName?: string
    /** Fires once the image has loaded or failed, e.g. to reveal a freshly uploaded avatar. */
    onImageSettled?: () => void
    textClassName?: string
}

const sizeVariants: Record<AvatarSize, {container: string; image: number; optimized: number; text: string}> = {
    small: {
        container: 'h-9 w-9',
        image: 36,
        optimized: 72,
        text: 'text-sm font-semibold'
    },
    medium: {
        container: 'h-12 w-12',
        image: 48,
        optimized: 96,
        text: 'text-base font-semibold'
    },
    large: {
        container: 'h-24 w-24',
        image: 96,
        optimized: 192,
        text: 'text-2xl font-bold'
    },
    xlarge: {
        container: 'size-24 sm:size-32 lg:size-auto lg:w-full lg:aspect-square',
        image: 296,
        optimized: 592,
        text: 'text-3xl font-bold lg:text-7xl'
    }
}

const NEXT_IMAGE_HOSTS = new Set(['img.clerk.com', 'images.clerk.dev'])

function getInitials(name?: string | null): string {
    const normalized = name?.trim()
    if (!normalized) return 'A'

    const words = normalized.split(/\s+/)
    if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase()
    }

    return `${words[0][0] ?? ''}${words[words.length - 1][0] ?? ''}`.toUpperCase() || 'A'
}

function appendCacheBuster(url: string, cacheBuster?: string | number | null): string {
    if (cacheBuster === null || cacheBuster === undefined || cacheBuster === '') {
        return url
    }

    try {
        const parsed = new URL(url)
        parsed.searchParams.set('v', String(cacheBuster))
        return parsed.toString()
    } catch {
        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}v=${cacheBuster}`
    }
}

function canUseNextImage(src?: string): boolean {
    if (!src) return false
    if (src.startsWith('/')) return true

    try {
        const parsed = new URL(src)
        return NEXT_IMAGE_HOSTS.has(parsed.hostname)
    } catch {
        return false
    }
}

export const Avatar: React.FC<AvatarProps> = ({
    name,
    src,
    alt,
    size = 'medium',
    loading = 'lazy',
    sizes,
    cacheBuster,
    className,
    fallbackClassName,
    textClassName,
    onImageSettled,
    ...props
}) => {
    const [hasImageError, setHasImageError] = useState(false)
    const imageRef = useRef<HTMLImageElement>(null)
    const sizeConfig = sizeVariants[size]
    const initials = getInitials(name)

    const optimizedSrc = useMemo(() => {
        if (!src) return undefined
        const resolvedSrc = appendCacheBuster(src, cacheBuster)
        return optimizeClerkImageUrl(resolvedSrc, {
            width: sizeConfig.optimized,
            height: sizeConfig.optimized
        }) ?? resolvedSrc
    }, [cacheBuster, sizeConfig.optimized, src])

    useEffect(() => {
        setHasImageError(false)
    }, [optimizedSrc])

    const shouldUseNextImage = canUseNextImage(optimizedSrc)
    const showImage = Boolean(optimizedSrc) && !hasImageError

    // Settling is reported per image change, not per render, so a caller can hide the avatar while a new one uploads.
    const onImageSettledRef = useRef(onImageSettled)
    useEffect(() => {
        onImageSettledRef.current = onImageSettled
    }, [onImageSettled])

    useEffect(() => {
        // An SSR image can fail before React attaches the onError handler.
        const image = imageRef.current
        if (image?.complete && image.naturalWidth === 0) setHasImageError(true)
        else if (!showImage || image?.complete) onImageSettledRef.current?.()
    }, [optimizedSrc, showImage])

    return (
        <div
            className={cn(
                'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-100 font-semibold text-primary-700 dark:bg-primary-900/35 dark:text-primary-200',
                sizeConfig.container,
                !showImage && fallbackClassName,
                className
            )}
            aria-label={name ?? alt ?? 'Avatar'}
            title={name ?? alt ?? 'Avatar'}
            {...props}
        >
            {showImage && shouldUseNextImage ? (
                <Image
                    ref={imageRef}
                    src={optimizedSrc!}
                    alt={alt ?? name ?? 'Avatar'}
                    width={sizeConfig.image}
                    height={sizeConfig.image}
                    sizes={sizes ?? `${sizeConfig.image}px`}
                    unoptimized
                    className="h-full w-full object-cover"
                    onLoad={onImageSettled}
                    onError={() => setHasImageError(true)}
                    loading={loading}
                />
            ) : null}

            {showImage && !shouldUseNextImage ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                    ref={imageRef}
                    src={optimizedSrc!}
                    alt={alt ?? name ?? 'Avatar'}
                    width={sizeConfig.image}
                    height={sizeConfig.image}
                    className="h-full w-full object-cover"
                    onLoad={onImageSettled}
                    onError={() => setHasImageError(true)}
                    loading={loading}
                    decoding="async"
                />
            ) : null}

            {!showImage ? (
                <span className={cn(sizeConfig.text, 'leading-none', textClassName)}>
                    {initials}
                </span>
            ) : null}
        </div>
    )
}

Avatar.displayName = 'Avatar'
